/**
 * Service for handling academic plan applications
 */

const ACADEMIC_API_URL =
  process.env.REACT_APP_ACADEMIC_API_URL || 'https://o0vnudbmyc.execute-api.eu-central-1.amazonaws.com';

export interface AcademicApplicationResponse {
  message: string;
  status: 'pending' | 'approved' | 'error';
  error?: string;
  contactSupport?: boolean;
}

export type AcademicStatus = null | 'pending' | 'approved' | 'active';
export type TrialType = 'academic' | 'commercial' | null;
export type UserType = 'academic' | 'commercial' | null;

export interface AcademicStatusResponse {
  status: AcademicStatus;
  groups: string[];
  trialType?: TrialType;
  userType?: UserType;
  activeCampaignLists?: string[];
  activeCampaignTags?: string[];
  nextPaymentDue?: string | null;
  createdAt?: string | null;
  extendedTrialDays?: number;
  trialExtendedAt?: string | null;
  academicActivationDeferred?: boolean;
}

export interface ActivateAcademicResponse {
  message: string;
  status: 'active' | 'error';
  alreadyActive?: boolean;
  error?: string;
}

export interface RevertAcademicToTrialResponse {
  message: string;
  status: 'deferred_trial' | 'error';
  error?: string;
  code?: string;
}

export interface ActiveCampaignFieldResponse {
  fieldValue: string | null;
  error?: string;
}

export interface ExtendCommercialTrialResponse {
  message: string;
  extendedDays?: number;
  status: 'success' | 'error';
  error?: string;
}

const ACADEMIC_APPROVED_TAGS = new Set(['6844', '6877']);

/** Pre-approved academic on trial: API flag or approved AC tag while still ROLE_TRIAL. */
export function inferAcademicActivationDeferred(response: AcademicStatusResponse): boolean {
  if (response.academicActivationDeferred) {
    return true;
  }

  const groups = response.groups ?? [];
  const tags = (response.activeCampaignTags ?? []).map(tag => String(tag));

  return (
    response.status === 'approved' &&
    groups.includes('ROLE_TRIAL') &&
    !groups.includes('ROLE_ACADEMIC') &&
    tags.some(tag => ACADEMIC_APPROVED_TAGS.has(tag))
  );
}

class AcademicApplicationService {
  private async parseResponse(response: Response): Promise<any> {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return null;
    }

    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * Apply for academic plan (for existing users with expired trial)
   */
  async applyForAcademic(email: string): Promise<AcademicApplicationResponse> {
    try {
      const response = await fetch(`${ACADEMIC_API_URL}/apply-existing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await this.parseResponse(response);

      if (!response.ok) {
        return {
          message:
            data?.error ||
            data?.message ||
            'Failed to submit application. Please try again in a few minutes.',
          status: 'error',
          error: data?.error || data?.message || `Request failed with status ${response.status}`,
          contactSupport: data?.contactSupport ?? response.status >= 500,
        };
      }

      return {
        message: data?.message || 'Application submitted successfully',
        status: data?.status || 'pending',
      };
    } catch (error) {
      console.error('Error applying for academic plan:', error);
      return {
        message: 'An error occurred while submitting your application. Please try again.',
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get academic status for a user
   * Returns: null (can apply), 'pending' (waiting approval), 'approved' (needs payment), 'active' (is academic)
   */
  async getAcademicStatus(email: string): Promise<AcademicStatusResponse> {
    try {
      const response = await fetch(`${ACADEMIC_API_URL}/academic-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to get academic status');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting academic status:', error);
      return {
        status: null,
        groups: [],
      };
    }
  }

  async getActiveCampaignField(email: string, fieldId: number): Promise<ActiveCampaignFieldResponse> {
    try {
      if (fieldId === 22) {
        const statusResponse = await this.getAcademicStatus(email);
        return {
          fieldValue: statusResponse.nextPaymentDue || null,
        };
      }

      return {
        fieldValue: null,
        error: 'Field not supported',
      };
    } catch (error) {
      console.error(`Error getting ActiveCampaign field ${fieldId}:`, error);
      return {
        fieldValue: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Activate deferred academic approval (opt-in or trial expiry).
   */
  async activateAcademic(email: string): Promise<ActivateAcademicResponse> {
    try {
      const response = await fetch(`${ACADEMIC_API_URL}/activate-academic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await this.parseResponse(response);

      if (!response.ok) {
        return {
          message: data?.error || 'Failed to activate academic access. Please try again.',
          status: 'error',
          error: data?.error || data?.code || `Request failed with status ${response.status}`,
        };
      }

      return {
        message: data?.message || 'Academic access activated',
        status: 'active',
        alreadyActive: data?.alreadyActive,
      };
    } catch (error) {
      console.error('Error activating academic access:', error);
      return {
        message: 'An error occurred while activating your academic access. Please try again.',
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async revertAcademicToTrial(email: string): Promise<RevertAcademicToTrialResponse> {
    try {
      const response = await fetch(`${ACADEMIC_API_URL}/revert-academic-to-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await this.parseResponse(response);

      if (!response.ok) {
        return {
          message: data?.error || 'Failed to restore trial access. Please try again.',
          status: 'error',
          error: data?.error || data?.code || `Request failed with status ${response.status}`,
          code: data?.code,
        };
      }

      return {
        message: data?.message || 'Trial access restored',
        status: 'deferred_trial',
      };
    } catch (error) {
      console.error('Error reverting academic to trial:', error);
      return {
        message: 'An error occurred while restoring your trial access. Please try again.',
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async extendCommercialTrial(email: string): Promise<ExtendCommercialTrialResponse> {
    try {
      const response = await fetch(`${ACADEMIC_API_URL}/extend-commercial-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await this.parseResponse(response);

      if (!response.ok) {
        return {
          message: data?.error || 'Failed to extend trial. Please try again.',
          status: 'error',
          error: data?.error || `Request failed with status ${response.status}`,
        };
      }

      return {
        message: data?.message || 'Trial extended successfully',
        extendedDays: data?.extendedDays,
        status: 'success',
      };
    } catch (error) {
      console.error('Error extending commercial trial:', error);
      return {
        message: 'An error occurred while extending your trial. Please try again.',
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Check if user has academic approval pending payment
   * @deprecated Use getAcademicStatus instead
   */
  hasAcademicApproved(user: any): boolean {
    return user?.['custom:academic_approved'] === 'true';
  }

  /**
   * Check if user has pending academic application
   * @deprecated Use getAcademicStatus instead
   */
  hasAcademicPending(user: any): boolean {
    return user?.['custom:academic_pending'] === 'true';
  }
}

const academicApplicationService = new AcademicApplicationService();
export default academicApplicationService;
